/**
 * MODULE: useAssistantConnectors.ts
 * PURPOSE: Load, persist, and toggle assistant connector sources (flows + MCP).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAssistantConnectorCatalog } from './assistantConnectorsApi';
import {
  DEFAULT_CONNECTOR_SELECTION,
  normalizeConnectorSelection,
  type AssistantConnectorItem,
  type AssistantConnectorSelection,
} from './assistantConnectorTypes';
import { isConnectorSelected, toggleConnectorInSelection } from './assistantConnectorOutbound';

const STORAGE_PREFIX = 'brazn:assistant-connectors';

function storageKey(scope: string | null): string | null {
  if (!scope || scope === 'anon') return null;
  return `${STORAGE_PREFIX}:${scope}`;
}

function readStoredSelection(scope: string | null): AssistantConnectorSelection {
  const key = storageKey(scope);
  if (!key || typeof localStorage === 'undefined') return DEFAULT_CONNECTOR_SELECTION;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_CONNECTOR_SELECTION;
    return normalizeConnectorSelection(JSON.parse(raw) as AssistantConnectorSelection);
  } catch {
    return DEFAULT_CONNECTOR_SELECTION;
  }
}

function writeStoredSelection(scope: string | null, selection: AssistantConnectorSelection): void {
  const key = storageKey(scope);
  if (!key || typeof localStorage === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(selection));
}

export function useAssistantConnectors(persistenceScope: string | null) {
  const [catalog, setCatalog] = useState<AssistantConnectorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<AssistantConnectorSelection>(() =>
    readStoredSelection(persistenceScope)
  );

  useEffect(() => {
    setSelection(readStoredSelection(persistenceScope));
  }, [persistenceScope]);

  useEffect(() => {
    writeStoredSelection(persistenceScope, selection);
  }, [persistenceScope, selection]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await fetchAssistantConnectorCatalog();
      setCatalog(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!persistenceScope || persistenceScope === 'anon') return;
    void reload();
  }, [persistenceScope, reload]);

  const toggle = useCallback((item: AssistantConnectorItem) => {
    setSelection((prev) => toggleConnectorInSelection(item, prev));
  }, []);

  const isSelected = useCallback(
    (item: AssistantConnectorItem) => isConnectorSelected(item, selection),
    [selection]
  );

  const flowConnectors = useMemo(
    () => catalog.filter((c) => c.kind === 'langflow_flow'),
    [catalog]
  );
  const mcpConnectors = useMemo(
    () => catalog.filter((c) => c.kind === 'mcp_tool'),
    [catalog]
  );

  return {
    catalog,
    flowConnectors,
    mcpConnectors,
    selection,
    loading,
    error,
    reload,
    toggle,
    isSelected,
  };
}
